// Path: ./src/app/pages/CameraPage/index.tsx
import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CameraWrapper, ColorButton, StartButton, Video } from "./styled";
import CameraIcon from "@mui/icons-material/Camera";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Stack } from "@mui/material";
import { saveSplittedStoreUploadedImage } from "../../../utils/storeUploadImage/SaveStoreUploadedImage";
import { adminApiService } from "../../../utils/api";
import { GoFlyerAdminContext } from "../../../contextApi/context";
import { GfStore } from "@swagger/typescript-fetch-goflyer";
import { useLoading } from "../../../contextApi/LoadingContext";
import ImageWidthModal from "../../components/ImageWithModal";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { routes } from "../../../utils/constant/navigationRoutes";

type ImageData = {
  src: string;
  id: number;
};

/**
 * React functional component representing the Camera Page for capturing and uploading store images.
 * @returns {JSX.Element} The Camera Page component.
 */
const CameraPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { state } = useContext(GoFlyerAdminContext);
  const params = useParams();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [images, setImages] = useState<ImageData[]>([]);
  const [storeDetails, setStoreDetails] = useState<GfStore>();
  const { showLoadingScreen } = useLoading();
  const [streamStarted, setStreamStarted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const startVideo = async () => {
    try {
      const constraints = {
        video: { facingMode: "environment" }, // Request the back camera
      };
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          setStreamStarted(true);
        }
      }
    } catch (error) {
      console.error("Error accessing the device camera:", error);
      // You may want to handle the error by trying to access the front camera or displaying an error message to the user.
    }
  };

  const takePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement("canvas");
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        const imageUrl = canvas.toDataURL("image/png");
        setImages([...images, { src: imageUrl, id: images.length }]);
      }
    }
  };

  /**
   * Converts a base64-encoded string to a File object.
   * @param {string} base64String - The base64-encoded string.
   * @returns {File} The converted File object.
   */
  const convertBase64ToFile = (base64String: string): File => {
    const parts = base64String.split(";base64,");
    const contentType = parts[0].split(":")[1];
    const rawImageData = window.atob(parts[1]);

    const uint8Array = new Uint8Array(rawImageData.length);
    for (let i = 0; i < rawImageData.length; i++) {
      uint8Array[i] = rawImageData.charCodeAt(i);
    }
    const blob = new Blob([uint8Array], { type: contentType });
    const file = new File([blob], "image.png", { type: contentType });

    return file;
  };

  /**
   * Saves the captured photos to the server.
   * Converts images to File objects,  creates GfFlyerItemImage records,
   * Navigates to the success page upon successful upload.
   * @returns {void}
   */
  const savePhotos = async () => {
    showLoadingScreen(true);
    if (!storeDetails?.id) {
      return;
    }
    const filesArray = images.map((value) => {
      return convertBase64ToFile(value.src);
    });
    const splitImageResult = await saveSplittedStoreUploadedImage(filesArray);
    const gfImagesResponse =
      await adminApiService.createManyBaseGfImageAdminControllerGfImage({
        bulk: splitImageResult,
      });

    if (gfImagesResponse?.length) {
      const result = await gfImagesResponse.map(async (value) => {
        return await adminApiService.createOneBaseGfFlyerItemImageAdminControllerGfFlyerItemImage(
          {
            adminUser: state?.gfAdminUser ? state?.gfAdminUser : undefined,
            gfImage: value,
            store: storeDetails,
          },
        );
      });

      await Promise.all(result);
    }
    showLoadingScreen(false);
    navigate(routes.camera.success);
  };

  /**
   * Clears the captured images, allowing the user to retake photos.
   * @returns {void}
   */
  const retakePhotos = () => {
    setImages([]);
  };

  /**
   * Retrieves and sets details for the specified store.
   * Redirects to the home page if the store details are not found.
   * @param {string} storeId - The ID of the store.
   * @returns {void}
   */
  const getStoreDetail = async (storeId: string) => {
    const storeResult =
      await adminApiService.getOneBaseGfStoreAdminControllerGfStore(storeId);

    if (!storeResult?.id) {
      navigate(routes.core.root);
    }
    setStoreDetails(storeResult);
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const promises: Promise<string>[] = [];
      for (let i = 0; i < files.length; i++) {
        promises.push(readFile(files[i]));
      }
      const base64Images = await Promise.all(promises);
      const results = base64Images?.map((value, index) => ({
        src: value,
        id: images.length + (index + 1),
      }));

      setImages([...images, ...results]);
    }
  };

  const readFile = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to read file"));
        }
      };
      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  useEffect(() => {
    params?.storeId && getStoreDetail(params?.storeId);
  }, [params]);

  console.log(`streamStarted`, streamStarted);

  return (
    <Stack
      display="flex"
      justifyContent="center"
      alignItems="center"
      mt={20}
      flexDirection="column"
      data-testid="camerapage-test-id"
    >
      {!streamStarted && (
        <StartButton onClick={startVideo}>
          {t(messages.Take_a_photo_and_upload_this_weeks_flyer_specials())}
        </StartButton>
      )}
      <CameraWrapper>
        <Video
          ref={videoRef}
          style={{ display: streamStarted ? "block" : "none" }}
          autoPlay={streamStarted}
          playsInline
        ></Video>
        {streamStarted && (
          <Stack
            direction={"row"}
            mt={3}
            position={"absolute"}
            bottom={10}
            justifyContent={"space-around"}
            width={"100%"}
            paddingLeft={2}
            paddingRight={2}
          >
            <ColorButton onClick={retakePhotos} disabled={!images.length}>
              <RestartAltIcon />
              {t(messages.retake())}
            </ColorButton>
            <ColorButton onClick={takePhoto}>
              <CameraIcon /> {t(messages.Capture())}
            </ColorButton>
            <ColorButton onClick={handleButtonClick}>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                ref={fileInputRef}
                style={{ display: "none" }}
                multiple={true}
              />
              <UploadFileIcon /> {t(messages.Photos())}
            </ColorButton>
            <ColorButton onClick={savePhotos} disabled={!images.length}>
              <CloudUploadIcon />
              {t(messages.upload())}
            </ColorButton>
          </Stack>
        )}
      </CameraWrapper>
      <Stack direction={"column"} alignItems={"center"}>
        <Stack
          flexWrap={"wrap"}
          alignItems={"center"}
          justifyContent={"center"}
          direction={"row"}
          mt={7}
        >
          {images.length > 0 &&
            images.map((value: ImageData, index: number) => {
              return (
                <div
                  key={index}
                  style={{ margin: "5px" }}
                  data-testid="camerapage-test-id"
                >
                  <ImageWidthModal
                    smallImageUrl={value.src}
                    largeImageUrl={value.src}
                    alt={`${index + 1}`}
                  />
                </div>
              );
            })}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default CameraPage;
